import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import {
  IMAGE_MAX_KB,
  validateContactDetail,
  validateEmployeeId,
  validateEmployeeName,
  validateEmail,
  validateAadharNumber,
  validatePanNumber,
  validateImageFilesUnderSize,
  validateDateNotAfterToday,
  validateRequired,
  todayDateInputValue,
} from '../../utils/validation';
import {
  useGetActiveDepartmentsQuery,
  useGetActiveDesignationsQuery,
  useGetLocationsByDepartmentQuery,
  useGetEmployeeByIdQuery,
  useUpdateEmployeeMutation,
} from '../../redux/employeeApi';
import { accountImageDisplayUrl } from '../../utils/accountImageUrl';
import {
  accountableDepartmentIdsFromAccount,
  locationIdsFromAccount,
  locationNamesFromAccount,
} from '../../utils/accountRoleClassification';
import { employeeListBackPath } from '../../utils/employeeListState';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const RequiredStar = () => <span className="text-red-600">*</span>;

const LEVEL_TYPE_OPTIONS = [
  { value: 'level1', label: 'Level 1' },
  { value: 'level2', label: 'Level 2' },
  { value: 'level3', label: 'Level 3' },
  { value: 'level4', label: 'Level 4' },
];

const EMPLOYMENT_NATURE_OPTIONS = [
  { value: 'gspl', label: 'GSPL' },
  { value: 'vendor', label: 'Vendor' },
];

const COST_CENTRE_TYPE_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant Operations' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'corporate', label: 'Corporate' },
];

function normalizeEmploymentNature(value, fallbackOutsource) {
  const compact = String(value || '').trim().toLowerCase();
  if (compact === 'gspl' || compact === 'vendor') return compact;
  if (fallbackOutsource === true || fallbackOutsource === 'true') return 'vendor';
  if (fallbackOutsource === false || fallbackOutsource === 'false') return 'gspl';
  return '';
}

function normalizeCostCentreType(value, fallbackProduction) {
  const compact = String(value || '').trim().toLowerCase();
  if (compact === 'restaurant' || compact === 'manufacturing' || compact === 'corporate') {
    return compact;
  }
  if (fallbackProduction === true || fallbackProduction === 'true') return 'manufacturing';
  return '';
}

function normalizeLevelType(value) {
  const compact = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (
    compact === 'level1' ||
    compact === 'level2' ||
    compact === 'level3' ||
    compact === 'level4'
  ) {
    return compact;
  }
  return '';
}

function toIdArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null || item === '') return '';
        if (typeof item === 'object') return String(item.id ?? item.location_id ?? '');
        return String(item);
      })
      .filter((v) => v !== '');
  }
  if (value == null || value === '') return [];
  if (typeof value === 'object') {
    const id = value.id ?? value.location_id;
    return id == null || id === '' ? [] : [String(id)];
  }
  return [String(value)];
}

function designationOptionLabel(d) {
  if (!d || typeof d !== 'object') return '';
  const fromRoles = Array.isArray(d.role_names)
    ? d.role_names
        .map((r) => (typeof r === 'string' ? r : r?.name))
        .filter(Boolean)
        .join(', ')
    : '';
  return (
    d.name ??
    d.designation_name ??
    d.title ??
    (fromRoles || undefined) ??
    String(d.id ?? '')
  );
}

function supervisorOptionId(s) {
  if (!s || typeof s !== 'object') return '';
  const id = s.id ?? s.account_id ?? s.reporting_supervisor_id;
  return id == null || id === '' ? '' : String(id);
}

function supervisorOptionName(s) {
  if (!s || typeof s !== 'object') return '';
  return String(
    s.name ?? s.reporting_supervisor_name ?? s.employee_name ?? s.account_name ?? ''
  ).trim();
}

function supervisorNamesMatch(a, b) {
  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  const left = normalize(a);
  const right = normalize(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const stripCode = (value) => value.replace(/\s*\([^)]*\)\s*$/, '').trim();
  const leftName = stripCode(left);
  const rightName = stripCode(right);
  return Boolean(leftName && rightName && leftName === rightName);
}

function getInputClass(hasError) {
  return `${inputClasses}${
    hasError ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''
  }`;
}

function AdminEmployeeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isOutsourceEdit = String(location.pathname || '').includes('/outsource-employee/');
  const listPath = employeeListBackPath(location, { isOutsource: isOutsourceEdit });
  const listLabel = isOutsourceEdit ? 'Helper Employees' : 'Employees';
  const employeeId = Number(id);
  const { data: departmentsRes } = useGetActiveDepartmentsQuery();
  const { data: employeeRes } = useGetEmployeeByIdQuery(employeeId, {
    skip: Number.isNaN(employeeId),
  });
  const [updateEmployee, { isLoading }] = useUpdateEmployeeMutation();

  const account = useMemo(() => employeeRes?.data || null, [employeeRes]);

  const [form, setForm] = useState({
    empId: '',
    empName: '',
    email: '',
    contact: '',
    isReportingSupervisor: false,
    employmentNature: '',
    costCentreType: '',
    levelType: '',
    departmentIds: [],
    designationId: '',
    reportingSupervisorId: '',
    locationIds: [],
    dob: '',
    doj: '',
    aadhar: '',
    pan: '',
    status: true,
  });
  const [empImage, setEmpImage] = useState(null);
  const [aadharImage, setAadharImage] = useState(null);
  const [panImage, setPanImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [empPreviewUrl, setEmpPreviewUrl] = useState('');
  const [aadharPreviewUrl, setAadharPreviewUrl] = useState('');
  const [panPreviewUrl, setPanPreviewUrl] = useState('');
  const empImageInputRef = useRef(null);
  const aadharImageInputRef = useRef(null);
  const panImageInputRef = useRef(null);

  const primaryDepartmentId = form.departmentIds[0] || '';
  const primaryLocationId = form.locationIds[0] || '';

  const { data: locationsByDeptRes } = useGetLocationsByDepartmentQuery(
    { accountable_department_id: primaryDepartmentId },
    { skip: !primaryDepartmentId }
  );
  const { data: designationsRes } = useGetActiveDesignationsQuery(form.departmentIds, {
    skip: form.departmentIds.length === 0,
  });

  const locationOptions = useMemo(() => {
    const rows = locationsByDeptRes?.data || [];
    return rows.map((loc) => ({
      value: String(loc.id ?? loc.location_id),
      label: loc.name ?? loc.location_name ?? String(loc.id ?? loc.location_id),
    }));
  }, [locationsByDeptRes?.data]);

  const reportingSupervisorOptions = useMemo(() => {
    const locations = locationsByDeptRes?.data || [];
    const byId = new Map();

    if (form.locationIds.length) {
      const selectedSet = new Set(form.locationIds.map(String));
      for (const loc of locations) {
        if (!selectedSet.has(String(loc.id ?? loc.location_id))) continue;
        const supervisors = Array.isArray(loc.reporting_supervisors)
          ? loc.reporting_supervisors
          : [];
        for (const s of supervisors) {
          const id = supervisorOptionId(s);
          if (!id || byId.has(id)) continue;
          byId.set(id, {
            ...s,
            id,
            name: supervisorOptionName(s) || id,
          });
        }
      }
    }

    // Keep currently saved supervisor visible even if location API omits it.
    const currentId = String(
      form.reportingSupervisorId ||
        account?.reporting_supervisor_id ||
        account?.reporting_supervisor?.id ||
        ''
    ).trim();
    if (currentId && !byId.has(currentId)) {
      const currentName =
        account?.reporting_supervisor_name ||
        account?.reporting_supervisor?.name ||
        currentId;
      byId.set(currentId, { id: currentId, name: String(currentName) });
    }

    return Array.from(byId.values());
  }, [
    locationsByDeptRes?.data,
    form.locationIds,
    form.reportingSupervisorId,
    account,
  ]);

  useEffect(() => {
    if (!account) return;
    const isReportingSupervisor = Boolean(
      account.is_reporting_supervisor ?? account.isReportingSupervisor
    );
    const departmentIds = accountableDepartmentIdsFromAccount(account).slice(0, 1);
    const locationIds = locationIdsFromAccount(account);
    const natureFromApi = normalizeEmploymentNature(
      account.employment_nature ?? account.employmentNature
    );
    const costFromApi = normalizeCostCentreType(
      account.cost_centre_type ?? account.costCentreType
    );
    let employmentNature = natureFromApi;
    let costCentreType = costFromApi;
    if (!employmentNature && !costCentreType) {
      const outsource = account.is_outsource ?? account.isOutsource;
      const production = account.is_production ?? account.isProduction;
      if (outsource === true || outsource === 'true') employmentNature = 'vendor';
      else if (outsource === false || outsource === 'false') employmentNature = 'gspl';
      if (production === true || production === 'true') costCentreType = 'manufacturing';
    }
    setForm({
      empId: account.employee_id || '',
      empName: account.name || '',
      email: account.email || '',
      contact: account.contact || '',
      isReportingSupervisor,
      employmentNature,
      costCentreType,
      levelType: normalizeLevelType(account.level_type ?? account.levelType ?? ''),
      departmentIds,
      designationId: String(
        account.role_id ?? account.designation_id ?? account.role?.id ?? account.designation?.id ?? ''
      ),
      reportingSupervisorId: String(
        account.reporting_supervisor_id ?? account.reporting_supervisor?.id ?? ''
      ),
      locationIds,
      dob: account.dob || '',
      doj: account.date_of_joining || '',
      aadhar: account.aadhar_no || '',
      pan: String(account.pan_number || '').toUpperCase(),
      status: Boolean(account.status),
    });
  }, [account]);

  // Some employee list payloads include names but omit *_id fields.
  // Backfill select values from loaded options so edit form shows selected entries.
  useEffect(() => {
    const options = designationsRes?.data || [];
    if (!options.length) return;
    const current = String(form.designationId || '');
    const hasCurrent = current && options.some((d) => String(d.id ?? d.role_id) === current);
    if (hasCurrent) return;

    const accountDesignation = String(
      account?.role_name ||
        account?.designation_name ||
        (typeof account?.designation === 'object' ? account?.designation?.name : account?.designation) ||
        ''
    )
      .trim()
      .toLowerCase();
    if (!accountDesignation) return;

    const matched = options.find((d) => designationOptionLabel(d).trim().toLowerCase() === accountDesignation);
    if (!matched) return;

    setForm((prev) => {
      const nextValue = String(matched.id ?? matched.role_id ?? '');
      if (!nextValue || String(prev.designationId || '') === nextValue) return prev;
      return { ...prev, designationId: nextValue };
    });
  }, [designationsRes, form.designationId, account]);

  useEffect(() => {
    const options = locationOptions;
    if (!options.length) return;
    const current = form.locationIds.map(String);
    const hasCurrent =
      current.length > 0 && current.every((id) => options.some((l) => String(l.value) === id));
    if (hasCurrent) return;
    if (form.isReportingSupervisor) return;

    const accountLocation = String(locationNamesFromAccount(account) || '')
      .trim()
      .toLowerCase();
    if (!accountLocation) return;

    // Match first location name when account has multiple comma-separated locations.
    const firstLocationName = accountLocation.split(',')[0]?.trim() || accountLocation;
    const matched = options.find(
      (l) => String(l.label || '').trim().toLowerCase() === firstLocationName
    );
    if (!matched) return;

    setForm((prev) => {
      const nextValue = String(matched.value ?? '');
      if (!nextValue || (prev.locationIds.length === 1 && prev.locationIds[0] === nextValue))
        return prev;
      return { ...prev, locationIds: [nextValue] };
    });
  }, [locationOptions, form.locationIds, form.isReportingSupervisor, account]);

  useEffect(() => {
    const options = reportingSupervisorOptions;
    if (!options.length) return;
    const current = String(form.reportingSupervisorId || '');
    const hasCurrent =
      current && options.some((s) => supervisorOptionId(s) === current);
    if (hasCurrent) return;

    const accountSupervisor = String(
      account?.reporting_supervisor_name || account?.reporting_supervisor?.name || ''
    ).trim();
    if (!accountSupervisor) return;

    const matched = options.find((s) =>
      supervisorNamesMatch(supervisorOptionName(s), accountSupervisor)
    );
    if (!matched) return;

    setForm((prev) => {
      const nextValue = supervisorOptionId(matched);
      if (!nextValue || String(prev.reportingSupervisorId || '') === nextValue) return prev;
      return { ...prev, reportingSupervisorId: nextValue };
    });
  }, [reportingSupervisorOptions, form.reportingSupervisorId, account]);

  const handleChange = (field) => (e) => {
    const raw = e?.target?.value;
    setForm((prev) => {
      if (field === 'isReportingSupervisor') {
        const next = String(raw) === 'true';
        return {
          ...prev,
          isReportingSupervisor: next,
          departmentIds: [],
          locationIds: [],
          designationId: '',
          reportingSupervisorId: '',
        };
      }
      if (field === 'departmentIds') {
        const nextIds = toIdArray(raw).slice(0, 1);
        return {
          ...prev,
          departmentIds: nextIds,
          designationId: '',
          locationIds: [],
          reportingSupervisorId: '',
        };
      }
      if (field === 'locationIds') {
        const nextIds = prev.isReportingSupervisor
          ? toIdArray(raw)
          : toIdArray(raw).slice(0, 1);
        return {
          ...prev,
          locationIds: nextIds,
          reportingSupervisorId: '',
        };
      }
      if (field === 'email') return { ...prev, [field]: String(raw).replace(/\s+/g, '').toLowerCase() };
      if (field === 'empId')
        return { ...prev, [field]: String(raw).replace(/[^A-Za-z0-9/]/g, '').slice(0, 50) };
      if (field === 'empName')
        return { ...prev, [field]: String(raw).replace(/[^A-Za-z ]/g, '').slice(0, 50) };
      if (field === 'contact')
        return { ...prev, [field]: String(raw).replace(/[^0-9]/g, '').slice(0, 10) };
      if (field === 'aadhar')
        return { ...prev, [field]: String(raw).replace(/[^0-9]/g, '').slice(0, 12) };
      if (field === 'pan')
        return {
          ...prev,
          pan: String(raw).replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10),
        };
      return { ...prev, [field]: raw };
    });
    setErrors((prev) => (prev?.[field] ? { ...prev, [field]: '' } : prev));
  };

  const handleFileChange = (field) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeErr = validateImageFilesUnderSize([file]);
    if (sizeErr) {
      toast.error(sizeErr);
      if (field === 'emp' && empImageInputRef.current) empImageInputRef.current.value = '';
      if (field === 'aadhar' && aadharImageInputRef.current)
        aadharImageInputRef.current.value = '';
      if (field === 'pan' && panImageInputRef.current) panImageInputRef.current.value = '';
      return;
    }

    if (field === 'emp') {
      setEmpImage(file);
      setErrors((prev) => ({ ...prev, empImage: '' }));
    } else if (field === 'aadhar') {
      setAadharImage(file);
      setErrors((prev) => ({ ...prev, aadharImage: '' }));
    } else {
      setPanImage(file);
      setErrors((prev) => ({ ...prev, panImage: '' }));
    }
  };

  const handleRemoveEmpImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEmpImage(null);
    if (empImageInputRef.current) empImageInputRef.current.value = '';
  };

  const handleRemoveAadharImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAadharImage(null);
    if (aadharImageInputRef.current) aadharImageInputRef.current.value = '';
  };

  const handleRemovePanImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPanImage(null);
    if (panImageInputRef.current) panImageInputRef.current.value = '';
  };

  useEffect(() => {
    if (!empImage) {
      setEmpPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(empImage);
    setEmpPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [empImage]);

  useEffect(() => {
    if (!aadharImage) {
      setAadharPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(aadharImage);
    setAadharPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [aadharImage]);

  useEffect(() => {
    if (!panImage) {
      setPanPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(panImage);
    setPanPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [panImage]);

  const empDisplayUrl =
    empPreviewUrl || accountImageDisplayUrl(account, 'employee_image');
  const aadharDisplayUrl =
    aadharPreviewUrl || accountImageDisplayUrl(account, 'aadhar_front_image');
  const panDisplayUrl = panPreviewUrl || accountImageDisplayUrl(account, 'pan_image');

  const validateForm = () => {
    const next = {};

    next.empId = validateEmployeeId(form.empId);

    next.empName = validateEmployeeName(form.empName);

    next.email = String(form.email || '').trim()
      ? validateEmail(form.email, 'Email id')
      : '';
    next.contact = String(form.contact || '').trim()
      ? validateContactDetail(form.contact)
      : '';

    next.employmentNature = validateRequired(form.employmentNature, 'Employment nature');

    next.departmentIds =
      form.departmentIds.length > 0 ? '' : 'Please enter department';
    next.designationId = validateRequired(form.designationId, 'Designation');
    next.locationIds =
      form.locationIds.length > 0
        ? ''
        : form.isReportingSupervisor
          ? 'Please select at least one location'
          : 'Please enter location';
    next.reportingSupervisorId = form.isReportingSupervisor
      ? ''
      : validateRequired(form.reportingSupervisorId, 'Reporting supervisor');
    next.dob = String(form.dob || '').trim()
      ? validateDateNotAfterToday(form.dob, 'Date of birth')
      : '';
    next.doj = validateRequired(form.doj, 'Date of joining');

    next.aadhar = String(form.aadhar || '').trim()
      ? validateAadharNumber(form.aadhar, 'Aadhar number')
      : '';
    next.pan = String(form.pan || '').trim()
      ? validatePanNumber(form.pan, 'PAN number')
      : '';

    next.empImage = '';
    next.aadharImage = '';
    next.panImage = '';

    const empSizeErr = empImage ? validateImageFilesUnderSize([empImage]) : '';
    const aadharSizeErr = aadharImage
      ? validateImageFilesUnderSize([aadharImage])
      : '';
    const panSizeErr = panImage ? validateImageFilesUnderSize([panImage]) : '';
    if (empSizeErr) next.empImage = empSizeErr;
    if (aadharSizeErr) next.aadharImage = aadharSizeErr;
    if (panSizeErr) next.panImage = panSizeErr;

    const hasAny = Object.values(next).some(Boolean);
    setErrors(next);
    if (!hasAny) return { ok: true };

    const orderedKeys = [
      'empId',
      'empName',
      'email',
      'contact',
      'employmentNature',
      'costCentreType',
      'departmentIds',
      'designationId',
      'locationIds',
      'reportingSupervisorId',
      'dob',
      'doj',
      'aadhar',
      'pan',
      'empImage',
      'aadharImage',
      'panImage',
    ];

    const firstError =
      orderedKeys.map((k) => next[k]).find(Boolean) ||
      Object.values(next).find(Boolean) ||
      '';

    return { ok: false, firstError: String(firstError || '').trim() };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateForm();
    if (!validation.ok) {
      toast.error(
        validation.firstError ||
          'Please fix the highlighted fields (Images max ' + IMAGE_MAX_KB + 'KB).'
      );
      return;
    }

    const departmentIds = form.departmentIds.map(Number).filter((n) => Number.isFinite(n));
    const locationIds = form.locationIds.map(Number).filter((n) => Number.isFinite(n));

    const account = {
      employee_id: form.empId,
      name: form.empName,
      email: form.email,
      contact: form.contact,
      role_id: Number(form.designationId),
      is_reporting_supervisor: Boolean(form.isReportingSupervisor),
      employment_nature: normalizeEmploymentNature(form.employmentNature),
      cost_centre_type: normalizeCostCentreType(form.costCentreType),
      is_outsource: normalizeEmploymentNature(form.employmentNature) === 'vendor',
      is_production: normalizeCostCentreType(form.costCentreType) === 'manufacturing',
      ...(isOutsourceEdit ? {} : { level_type: normalizeLevelType(form.levelType) || undefined }),
      dob: form.dob || null,
      date_of_joining: form.doj,
      aadhar_no: form.aadhar || undefined,
      pan_number: form.pan.trim().toUpperCase() || undefined,
      status: form.status,
      employee_image: empImage || undefined,
      aadhar_front_image: aadharImage || undefined,
      pan_image: panImage || undefined,
    };

    account.accountable_department_ids = departmentIds;
    account.location_ids = locationIds;
    account.reporting_supervisor_id = form.reportingSupervisorId
      ? Number(form.reportingSupervisorId)
      : undefined;

    updateEmployee({
      id,
      account,
    })
      .unwrap()
      .then(() => {
        toast.success('Employee updated successfully.');
        navigate(listPath);
      })
      .catch((err) => toast.error(err?.data?.message || 'Failed to update employee.'));
  };

  if (!account) {
    return (
      <DashboardLayout title="Edit Employee">
        <div className="py-6 text-sm text-gray-600">Loading employee...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Edit Employee">
      <div className="space-y-4 py-4">
        <div className="flex items-center justify-end">
          <span className="text-sm text-gray-500">
            <Link to="/dashboard/admin" className="hover:text-brand">
              Home
            </Link>
            {' / '}
            <Link to={listPath} className="hover:text-brand">
              {listLabel}
            </Link>
            {' / '}
            <span className="text-brand">Edit Employee</span>
          </span>
        </div>
        <form onSubmit={handleSubmit} className="rounded-2xl bg-white px-6 py-6 shadow-sm">
          <h3 className="mb-4 text-xl font-semibold text-gray-800">Employee Information</h3>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Employee ID <RequiredStar />
              </span>
              <input
                type="text"
                value={form.empId}
                onChange={handleChange('empId')}
                className={getInputClass(Boolean(errors.empId))}
              />
              {errors.empId && <span className="mt-1 text-xs text-red-600">{errors.empId}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Employee Name <RequiredStar />
              </span>
              <input
                type="text"
                value={form.empName}
                onChange={handleChange('empName')}
                className={getInputClass(Boolean(errors.empName))}
              />
              {errors.empName && (
                <span className="mt-1 text-xs text-red-600">{errors.empName}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Email Id
              </span>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                className={getInputClass(Boolean(errors.email))}
              />
              {errors.email && <span className="mt-1 text-xs text-red-600">{errors.email}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Contact Number
              </span>
              <input
                type="text"
                value={form.contact}
                onChange={handleChange('contact')}
                inputMode="numeric"
                maxLength={10}
                className={getInputClass(Boolean(errors.contact))}
              />
              {errors.contact && (
                <span className="mt-1 text-xs text-red-600">{errors.contact}</span>
              )}
            </label>

            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">Is Reporting Supervisor</span>
              <SearchableSelect
                name="isReportingSupervisor"
                value={form.isReportingSupervisor ? 'true' : 'false'}
                onChange={handleChange('isReportingSupervisor')}
                className={getInputClass(false)}
                clearable={false}
                options={[
                  { value: 'false', label: 'No' },
                  { value: 'true', label: 'Yes' },
                ]}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Employment Nature <RequiredStar />
              </span>
              <SearchableSelect
                name="employmentNature"
                value={form.employmentNature}
                onChange={handleChange('employmentNature')}
                placeholder="Select"
                className={getInputClass(Boolean(errors.employmentNature))}
                options={EMPLOYMENT_NATURE_OPTIONS}
              />
              {errors.employmentNature && (
                <span className="mt-1 text-xs text-red-600">{errors.employmentNature}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Cost Centre Type
              </span>
              <SearchableSelect
                name="costCentreType"
                value={form.costCentreType}
                onChange={handleChange('costCentreType')}
                placeholder="Select"
                className={getInputClass(Boolean(errors.costCentreType))}
                options={COST_CENTRE_TYPE_OPTIONS}
              />
              {errors.costCentreType && (
                <span className="mt-1 text-xs text-red-600">{errors.costCentreType}</span>
              )}
            </label>
            {!isOutsourceEdit ? (
              <label className="flex flex-col text-sm text-gray-700">
                <span className="inline-flex items-center gap-1">Level Type</span>
                <SearchableSelect
                  name="levelType"
                  value={form.levelType}
                  onChange={handleChange('levelType')}
                  placeholder="Select"
                  className={getInputClass(false)}
                  options={LEVEL_TYPE_OPTIONS}
                />
              </label>
            ) : null}
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Accountable Department <RequiredStar />
              </span>
              <SearchableSelect
                name="departmentIds"
                value={form.departmentIds[0] || ''}
                onChange={handleChange('departmentIds')}
                className={getInputClass(Boolean(errors.departmentIds))}
                options={(departmentsRes?.data || []).map((d) => ({
                  value: String(d.id),
                  label:
                    d.name ??
                    d.department_name ??
                    d.title ??
                    d.department?.name ??
                    String(d.id),
                }))}
              />
              {errors.departmentIds && (
                <span className="mt-1 text-xs text-red-600">{errors.departmentIds}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Designation <RequiredStar />
              </span>
              <SearchableSelect
                name="designationId"
                value={form.designationId}
                onChange={handleChange('designationId')}
                className={getInputClass(Boolean(errors.designationId))}
                disabled={form.departmentIds.length === 0}
                options={(designationsRes?.data || []).map((d) => ({
                  value: String(d.id ?? d.role_id),
                  label: designationOptionLabel(d),
                }))}
              />
              {errors.designationId && (
                <span className="mt-1 text-xs text-red-600">{errors.designationId}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Location <RequiredStar />
              </span>
              <SearchableSelect
                name="locationIds"
                multiple={form.isReportingSupervisor}
                value={
                  form.isReportingSupervisor
                    ? form.locationIds
                    : form.locationIds[0] || ''
                }
                onChange={handleChange('locationIds')}
                className={getInputClass(Boolean(errors.locationIds))}
                disabled={!primaryDepartmentId}
                options={locationOptions}
              />
              {errors.locationIds && (
                <span className="mt-1 text-xs text-red-600">{errors.locationIds}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Reporting Supervisor (Manager)
                {!form.isReportingSupervisor ? <RequiredStar /> : null}
              </span>
              <SearchableSelect
                name="reportingSupervisorId"
                value={form.reportingSupervisorId}
                onChange={handleChange('reportingSupervisorId')}
                className={getInputClass(Boolean(errors.reportingSupervisorId))}
                disabled={!primaryLocationId}
                options={reportingSupervisorOptions.map((s) => ({
                  value: supervisorOptionId(s),
                  label: supervisorOptionName(s) || supervisorOptionId(s),
                }))}
              />
              {errors.reportingSupervisorId && (
                <span className="mt-1 text-xs text-red-600">{errors.reportingSupervisorId}</span>
              )}
            </label>

            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Date of Birth
              </span>
              <input
                type="date"
                value={form.dob}
                max={todayDateInputValue()}
                onChange={handleChange('dob')}
                className={getInputClass(Boolean(errors.dob))}
              />
              {errors.dob && <span className="mt-1 text-xs text-red-600">{errors.dob}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Date of Joining <RequiredStar />
              </span>
              <input
                type="date"
                value={form.doj}
                onChange={handleChange('doj')}
                className={getInputClass(Boolean(errors.doj))}
              />
              {errors.doj && <span className="mt-1 text-xs text-red-600">{errors.doj}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Aadhar Number
              </span>
              <input
                type="text"
                value={form.aadhar}
                onChange={handleChange('aadhar')}
                inputMode="numeric"
                maxLength={12}
                className={getInputClass(Boolean(errors.aadhar))}
              />
              {errors.aadhar && <span className="mt-1 text-xs text-red-600">{errors.aadhar}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                PAN Number
              </span>
              <input
                type="text"
                value={form.pan}
                onChange={handleChange('pan')}
                maxLength={10}
                autoCapitalize="characters"
                className={getInputClass(Boolean(errors.pan))}
              />
              {errors.pan && <span className="mt-1 text-xs text-red-600">{errors.pan}</span>}
            </label>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 inline-flex items-center gap-1">
                Employee Image
              </span>
              <div className="relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-dashed border-gray-300 bg-[#faf8f5]">
                <label
                  htmlFor="edit-emp-image"
                  className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
                >
                  {empDisplayUrl ? (
                    <img src={empDisplayUrl} alt="Employee" className="h-full w-full object-cover" />
                  ) : (
                    <span>
                      Click to browse
                      <br />
                      or drag &amp; drop file
                    </span>
                  )}
                </label>
                <input
                  id="edit-emp-image"
                  ref={empImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange('emp')}
                />
                {empImage && (
                  <button
                    type="button"
                    onClick={handleRemoveEmpImage}
                    className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                    aria-label="Remove new image selection"
                    title="Remove selection"
                  >
                    ×
                  </button>
                )}
              </div>
              {empImage && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-medium">{empImage.name}</span>
                </p>
              )}
              {errors.empImage && <p className="mt-1 text-xs text-red-600">{errors.empImage}</p>}
            </div>

            <div className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 inline-flex items-center gap-1">
                Aadhar Image
              </span>
              <div className="relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-dashed border-gray-300 bg-[#faf8f5]">
                <label
                  htmlFor="edit-aadhar-image"
                  className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
                >
                  {aadharDisplayUrl ? (
                    <img src={aadharDisplayUrl} alt="Aadhar" className="h-full w-full object-cover" />
                  ) : (
                    <span>
                      Click to browse
                      <br />
                      or drag &amp; drop file
                    </span>
                  )}
                </label>
                <input
                  id="edit-aadhar-image"
                  ref={aadharImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange('aadhar')}
                />
                {aadharImage && (
                  <button
                    type="button"
                    onClick={handleRemoveAadharImage}
                    className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                    aria-label="Remove new image selection"
                    title="Remove selection"
                  >
                    ×
                  </button>
                )}
              </div>
              {aadharImage && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-medium">{aadharImage.name}</span>
                </p>
              )}
              {errors.aadharImage && (
                <p className="mt-1 text-xs text-red-600">{errors.aadharImage}</p>
              )}
            </div>

            <div className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 inline-flex items-center gap-1">
                PAN Image
              </span>
              <div className="relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-dashed border-gray-300 bg-[#faf8f5]">
                <label
                  htmlFor="edit-pan-image"
                  className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
                >
                  {panDisplayUrl ? (
                    <img src={panDisplayUrl} alt="PAN" className="h-full w-full object-cover" />
                  ) : (
                    <span>
                      Click to browse
                      <br />
                      or drag &amp; drop file
                    </span>
                  )}
                </label>
                <input
                  id="edit-pan-image"
                  ref={panImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange('pan')}
                />
                {panImage && (
                  <button
                    type="button"
                    onClick={handleRemovePanImage}
                    className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                    aria-label="Remove new image selection"
                    title="Remove selection"
                  >
                    ×
                  </button>
                )}
              </div>
              {panImage && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-medium">{panImage.name}</span>
                </p>
              )}
              {errors.panImage && <p className="mt-1 text-xs text-red-600">{errors.panImage}</p>}
            </div>
          </div>
          <div className="flex justify-start gap-5 pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60"
            >
              {isLoading ? 'Saving...' : 'Update'}
            </button>

            <button
              type="button"
              onClick={() => navigate(listPath)}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default AdminEmployeeEdit;
